import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { volumePages } from '../data/volumePages';
import { PageLightbox } from './PageLightbox';
export function PageCarousel() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const last = volumePages.length - 1;
  const updateFromScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    if (max <= 0) {
      setActive(0);
      setAtStart(true);
      setAtEnd(true);
      return;
    }
    setActive(Math.round(track.scrollLeft / max * last));
    setAtStart(track.scrollLeft <= 4);
    setAtEnd(track.scrollLeft >= max - 4);
  }, [last]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    updateFromScroll();
    track.addEventListener('scroll', updateFromScroll, {
      passive: true
    });
    window.addEventListener('resize', updateFromScroll);
    return () => {
      track.removeEventListener('scroll', updateFromScroll);
      window.removeEventListener('resize', updateFromScroll);
    };
  }, [updateFromScroll]);
  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(last, index));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const padding = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    track.scrollTo({
      left: slide.offsetLeft - padding,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const closeLightbox = useCallback(() => setOpenIndex(null), []);
  const arrowClass = 'hidden h-12 w-12 flex-none items-center justify-center rounded-full bg-white text-[#5B2A45] shadow-soft transition-[background-color,color,opacity] duration-150 hover:bg-[#FF6B4A] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B4A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF7F4] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-[#5B2A45] lg:flex';
  return <section aria-labelledby="pages-heading" className="w-full bg-[#FAF7F4] font-sans" data-id="element-147" data-mp="21vz2dr17ofof">
      <div className="mx-auto max-w-[1100px] py-14 sm:py-20" data-id="element-148" data-mp="25xubhhpdvxcr">
        <div className="px-5 text-center" data-id="element-149" data-mp="25xax2ayxzr3h">
          <h2 id="pages-heading" className="text-[28px] font-bold leading-tight tracking-tight text-[#5B2A45] sm:text-4xl lg:text-[44px]" data-id="element-150" data-mp="1s77f3w73ijju">
            Look Inside Volume 5
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-slate sm:text-base" data-id="element-151" data-mp="2kubla4hxd3x7">
            Real pages from the pack. Every class is mapped step by step, with
            timings, cues and easier/harder options.
          </p>
        </div>

        <ul ref={trackRef} aria-label="Pages from Volume 5" className="mt-8 flex snap-x snap-mandatory scroll-pl-5 gap-4 overflow-x-auto px-5 pb-6 pt-2 [scrollbar-width:none] sm:mt-10 sm:gap-6 [&::-webkit-scrollbar]:hidden" data-id="element-152" data-mp="2uz9xrvlzb19g">
          {volumePages.map((page, i) => <li key={page.src} className="w-[80%] flex-none snap-start sm:w-[calc((100%-24px)/2)] lg:w-[calc((100%-48px)/3)]" aria-label={`${i + 1} of ${volumePages.length}: ${page.caption}`} data-id="element-153" data-mp="2g1rbczwrx056" data-mpi={i}>
              <figure className="relative h-full rounded-2xl bg-white p-3 shadow-soft" data-id="element-154" data-mp="2q1rscf767467">
                {i === 0 && <span className="absolute left-5 top-5 z-10 rounded-full bg-[#5B2A45] px-3 py-1 text-[11px] font-semibold text-white" data-id="element-155" data-mp="2l3glmdc699s7">
                    Volume 5 of 6
                  </span>}
                <button type="button" onClick={() => setOpenIndex(i)} aria-label={`Open full page: ${page.caption}`} className="block w-full cursor-zoom-in overflow-hidden rounded-xl bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B4A] focus-visible:ring-offset-2" data-id="element-156" data-mp="25z5esobmsj9o">
                  <img src={page.src} alt={page.alt} loading="lazy" decoding="async" width={1080} height={1920} className="aspect-[9/16] w-full object-contain" data-id="element-157" data-mp="2jwlme35nyj30" />
                </button>
                <figcaption className="px-1 pb-1 pt-3 text-[13px] leading-snug text-[#5B2A45]" data-id="element-158" data-mp="33n771kalchtt">
                  {page.caption}
                </figcaption>
              </figure>
            </li>)}
        </ul>

        <div className="mt-2 flex items-center justify-center gap-4 px-5" data-id="element-159" data-mp="3co59hy5dshaa">
          <button type="button" onClick={() => goTo(active - 1)} disabled={atStart} aria-label="Previous page" className={arrowClass} data-id="element-160" data-mp="110ihhu1c46g2">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-161" data-mp="s0lpbifqkpg9" />
          </button>

          <div className="flex items-center" role="group" aria-label="Choose a page" data-id="element-162" data-mp="n196r9vwe4jh">
            {volumePages.map((page, i) => <button key={page.src} type="button" onClick={() => goTo(i)} aria-label={`Go to page ${i + 1}: ${page.caption}`} aria-current={active === i ? 'true' : undefined} className="flex h-11 w-11 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B4A]" data-id="element-163" data-mp="1jpymatk1yl2a" data-mpi={i}>
                <span className={`block h-2.5 w-2.5 rounded-full transition-colors duration-200 ${active === i ? 'bg-[#FF6B4A]' : 'bg-[#5B2A45]/20'}`} data-id="element-164" data-mp="1yo0treu7fqot" />
              </button>)}
          </div>

          <button type="button" onClick={() => goTo(active + 1)} disabled={atEnd} aria-label="Next page" className={arrowClass} data-id="element-165" data-mp="2mlgivsgvi5rq">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-166" data-mp="2crg6r6n6c09i" />
          </button>
        </div>
      </div>

      <PageLightbox pages={volumePages} index={openIndex} onClose={closeLightbox} onIndexChange={setOpenIndex} data-id="element-167" data-mp="23sushnoqj7ej" />
    </section>;
}